import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IntlShape } from 'react-intl-next';

import Link from '@atlaskit/link';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { usePasteDetection } from '@confluence/editor-features/entry-points/usePasteDetection.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

const AI_RESULT_VIEW_TIMEOUT = 10000;
const AI_RESULT_VIEW_TIMEOUT_THRESHOLD = 0.5;

interface UseLivePagesAITitleGenerationProps {
	contentId: string;
	userOffline?: boolean;
	title: string;
	isUserEditing: boolean;
	onTitleUpdate: (value: string) => Promise<void>;
	flags?: FlagsStateContainer;
	formatMessage: IntlShape['formatMessage'];
	i18n: any;
	isEditorEmpty?: boolean;
}

interface UseLivePagesAITitleGenerationReturn {
	isGeneratingTitle: boolean;
	isTitleAIGenerated: boolean;
	documentContext: string;
	onAITitleChange: (newTitle: string) => void;
	resetAIGeneratedFlag: () => void;
}

export const useLivePagesAITitleGeneration = ({
	contentId,
	userOffline,
	title,
	isUserEditing,
	onTitleUpdate,
	flags,
	formatMessage,
	i18n,
	isEditorEmpty,
}: UseLivePagesAITitleGenerationProps): UseLivePagesAITitleGenerationReturn => {
	const { documentContext, pasteDetected } = usePasteDetection({
		contentId,
		userOffline,
		isEditorEmpty: isEditorEmpty ?? false,
	});

	const { trackAIInteractionInit, trackAIResultView } = useAIEventsInstrumentation();

	const [isGeneratingTitle, setIsGeneratingTitle] = useState(false);
	const [isTitleAIGenerated, setIsTitleAIGenerated] = useState(false);
	const prevIsGeneratingTitleRef = useRef(isGeneratingTitle);
	const prevIsTitleAIGeneratedRef = useRef(isTitleAIGenerated);
	const aiGeneratedTitleRef = useRef<string | null>(null);
	const hasCompletedGenerationRef = useRef(false);
	const originalAIGeneratedTitleLengthRef = useRef<number | null>(null);
	const aiResultViewTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const titleRef = useRef(title);

	const onAITitleChange = useCallback(
		(newTitle: string) => {
			if (!hasCompletedGenerationRef.current) {
				hasCompletedGenerationRef.current = true;
				aiGeneratedTitleRef.current = newTitle;
				void onTitleUpdate(newTitle);
			}
			setIsGeneratingTitle(false);
		},
		[onTitleUpdate],
	);

	const actualTitle = useMemo(() => {
		if (!pasteDetected && !isGeneratingTitle) {
			return null;
		}
		const titleInput = document.querySelector(
			'[data-ai-suggest-title-target="true"]',
		) as HTMLTextAreaElement | null;
		return titleInput?.value || '';
	}, [pasteDetected, isGeneratingTitle]);

	useEffect(() => {
		if (isGeneratingTitle) {
			hasCompletedGenerationRef.current = false;
		}
	}, [isGeneratingTitle]);

	useEffect(() => {
		if (
			pasteDetected &&
			!isGeneratingTitle &&
			!isUserEditing &&
			(!actualTitle || actualTitle.trim() === '')
		) {
			trackAIInteractionInit();
			setIsGeneratingTitle(true);
		}
	}, [pasteDetected, actualTitle, isGeneratingTitle, isUserEditing, trackAIInteractionInit]);

	useEffect(() => {
		if (aiGeneratedTitleRef.current && title === aiGeneratedTitleRef.current) {
			setIsTitleAIGenerated(true);
			originalAIGeneratedTitleLengthRef.current = aiGeneratedTitleRef.current.length;
			aiGeneratedTitleRef.current = null;
		}
	}, [title]);

	useEffect(() => {
		titleRef.current = title;
	}, [title]);

	useEffect(() => {
		if (!prevIsTitleAIGeneratedRef.current && isTitleAIGenerated) {
			if (aiResultViewTimeoutIdRef.current) {
				clearTimeout(aiResultViewTimeoutIdRef.current);
				aiResultViewTimeoutIdRef.current = null;
			}

			const originalLength = originalAIGeneratedTitleLengthRef.current ?? 0;

			aiResultViewTimeoutIdRef.current = setTimeout(() => {
				const currentTitleLength = titleRef.current?.length ?? 0;

				if (
					originalLength > 0 &&
					currentTitleLength >= originalLength * AI_RESULT_VIEW_TIMEOUT_THRESHOLD
				) {
					trackAIResultView({
						attributes: {
							aiFeatureName: 'aiTitleGenOnPaste',
							doesNotMeetMAUCriteria: true,
						},
					});
				}

				originalAIGeneratedTitleLengthRef.current = null;
				aiResultViewTimeoutIdRef.current = null;
			}, AI_RESULT_VIEW_TIMEOUT);
		} else if (prevIsTitleAIGeneratedRef.current && !isTitleAIGenerated) {
			if (aiResultViewTimeoutIdRef.current) {
				clearTimeout(aiResultViewTimeoutIdRef.current);
				aiResultViewTimeoutIdRef.current = null;
			}
		}

		prevIsTitleAIGeneratedRef.current = isTitleAIGenerated;
	}, [isTitleAIGenerated, trackAIResultView]);

	useEffect(() => {
		return () => {
			if (aiResultViewTimeoutIdRef.current) {
				clearTimeout(aiResultViewTimeoutIdRef.current);
				aiResultViewTimeoutIdRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		if (flags && prevIsGeneratingTitleRef.current && !isGeneratingTitle) {
			void flags.hideFlag('ai-generated-title-from-paste-flag');
			void flags.showInfoFlag({
				id: 'ai-generated-title-from-paste-flag',
				title: formatMessage(i18n.aiGeneratedTitleFromPaste),
				description: formatMessage(i18n.aiGeneratedTitleFromPasteDescription, {
					Link: (chunks: React.ReactNode[]) => (
						<Link target="_blank" href="https://www.atlassian.com/trust/atlassian-intelligence">
							{chunks}
						</Link>
					),
				}),
				actions: [
					{
						content: formatMessage(i18n.aiGeneratedTitleDismissButton),
						onClick: () => flags.hideFlag('ai-generated-title-from-paste-flag'),
					},
				],
			});
		}

		prevIsGeneratingTitleRef.current = isGeneratingTitle;
	}, [isGeneratingTitle, formatMessage, flags, i18n]);

	const resetAIGeneratedFlag = useCallback(() => {
		setIsTitleAIGenerated(false);
	}, []);

	return {
		isGeneratingTitle,
		isTitleAIGenerated,
		documentContext,
		onAITitleChange,
		resetAIGeneratedFlag,
	};
};
